/**
 * Cycle counts — `/inventory/counts`.
 *
 * Worksheet list plus the generation form. Generation snapshots the position
 * quantities server-side at `snapshot_at`; the snapshot never travels to the
 * counter, so nothing on this screen (or the entry screen) can leak it.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { Plus } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@erp/ui";

import { DataTable, badgeCell, dateTimeCell, textCell } from "@/components/data-table";
import { surfaceProblem } from "@/components/form";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  COUNT_STATUSES,
  createCountWorksheet,
  type CountWorksheet,
  type CountWorksheetCreate,
  type CountWorksheetStatus,
  type CountWorksheetSummary,
} from "../api";
import { isCountIncomplete } from "../problems";
import {
  inventoryKeys,
  useCountList,
  useLocationOptions,
  useWarehouseOptions,
  type CountFilters,
} from "../queries";
import { ALL, CategorySelect } from "../components/pickers";

const COUNT_BADGE_VARIANTS = {
  draft: "outline",
  counting: "warning",
  submitted: "default",
  approved: "success",
  cancelled: "secondary",
} as const;

function toLocalInputValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export interface CountsPageProps {
  canCreate?: boolean;
  canReadCategories?: boolean;
  onOpenWorksheet?: (worksheetId: string, status: CountWorksheetStatus) => void;
  pageLimit?: number;
}

export function CountsPage({
  canCreate = false,
  canReadCategories = false,
  onOpenWorksheet,
  pageLimit,
}: CountsPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<string>(ALL);
  const [warehouseFilter, setWarehouseFilter] = useState<string>(ALL);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [draftWarehouseId, setDraftWarehouseId] = useState<string | null>(null);
  const [draftSnapshotAt, setDraftSnapshotAt] = useState(() => toLocalInputValue(new Date()));
  const [draftCategoryId, setDraftCategoryId] = useState<string | null>(null);
  const [draftLocationIds, setDraftLocationIds] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  const warehouses = useWarehouseOptions().data ?? [];
  const warehouseCode = (id: string): string =>
    warehouses.find((warehouse) => warehouse.id === id)?.code ?? id;
  const draftLocations = useLocationOptions(draftWarehouseId, creating).data ?? [];

  const filters = useMemo<CountFilters>(() => {
    const built: CountFilters = {};
    if (status !== ALL) built.status = status as CountWorksheetStatus;
    if (warehouseFilter !== ALL) built.warehouse_id = warehouseFilter;
    if (categoryFilter !== null) built.category_id = categoryFilter;
    return built;
  }, [status, warehouseFilter, categoryFilter]);

  const query = useCountList(filters, pageLimit);

  const createMutation = useIdempotentMutation<CountWorksheet, CountWorksheetCreate>({
    mutationFn: (input, key) => createCountWorksheet(input, key),
    onSuccess: (worksheet) => {
      setCreating(false);
      setFormError(null);
      setDraftLocationIds([]);
      toast({
        variant: "success",
        title: t("inventory.counts.created", "Worksheet generated"),
      });
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.counts.all });
      onOpenWorksheet?.(worksheet.id, worksheet.status);
    },
    onError: (error) => {
      if (isCountIncomplete(error)) {
        setFormError(
          t(
            "inventory.counts.noLines",
            "That scope snapshots no positions — widen it and generate again.",
          ),
        );
        return;
      }
      surfaceProblem(error, t);
    },
  });

  const submitCreate = () => {
    if (draftWarehouseId === null || draftSnapshotAt === "") {
      setFormError(
        t("inventory.counts.createRequired", "Choose a warehouse and the snapshot time."),
      );
      return;
    }
    setFormError(null);
    createMutation.mutate({
      warehouse_id: draftWarehouseId,
      snapshot_at: new Date(draftSnapshotAt).toISOString(),
      ...(draftCategoryId === null ? {} : { category_id: draftCategoryId }),
      ...(draftLocationIds.length === 0 ? {} : { location_ids: draftLocationIds }),
    });
  };

  const columns = useMemo<ColumnDef<CountWorksheetSummary>[]>(
    () => [
      {
        accessorKey: "count_number",
        id: "count_number",
        header: t("inventory.counts.columns.number", "Worksheet"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono text-xs font-medium">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "warehouse_id",
        id: "warehouse",
        header: t("inventory.common.warehouse", "Warehouse"),
        enableSorting: false,
        cell: (ctx) => textCell(warehouseCode(ctx.getValue<string>())),
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("inventory.common.status", "Status"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(value, COUNT_BADGE_VARIANTS, t(`inventory.countStatus.${value}`, value));
        },
      },
      {
        accessorKey: "snapshot_at",
        id: "snapshot_at",
        header: t("inventory.counts.columns.snapshot", "Snapshot"),
        enableSorting: false,
        cell: (ctx) => dateTimeCell(ctx.getValue<string>()),
      },
      {
        accessorKey: "submitted_at",
        id: "submitted_at",
        header: t("inventory.counts.columns.submitted", "Submitted"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string | undefined>();
          return value === undefined ? textCell(undefined) : dateTimeCell(value);
        },
      },
      {
        accessorKey: "approved_at",
        id: "approved_at",
        header: t("inventory.counts.columns.approved", "Approved"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string | undefined>();
          return value === undefined ? textCell(undefined) : dateTimeCell(value);
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- warehouseCode derives from the loaded options
    [t, warehouses],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t("inventory.counts.title", "Cycle counts")}</h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "inventory.counts.subtitle",
              "Blind worksheets: counters never see the expected quantity, and approval posts the variances.",
            )}
          </p>
        </div>
        {canCreate ? (
          <Button
            type="button"
            onClick={() => {
              setCreating(true);
              setFormError(null);
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("inventory.counts.new", "New worksheet")}
          </Button>
        ) : null}
      </div>

      {creating ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("inventory.counts.newTitle", "Generate a worksheet")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="count-warehouse">
                  {t("inventory.counts.warehouseToCount", "Warehouse to count")}
                </Label>
                <Select
                  value={draftWarehouseId ?? ""}
                  onValueChange={(value) => {
                    setDraftWarehouseId(value);
                    setDraftLocationIds([]);
                  }}
                >
                  <SelectTrigger id="count-warehouse">
                    <SelectValue placeholder={t("inventory.common.pickWarehouse", "Warehouse…")} />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map((warehouse) => (
                      <SelectItem key={warehouse.id} value={warehouse.id}>
                        {warehouse.code} — {warehouse.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="count-snapshot-at">
                  {t("inventory.counts.snapshotAt", "Snapshot time")}
                </Label>
                <Input
                  id="count-snapshot-at"
                  type="datetime-local"
                  value={draftSnapshotAt}
                  onChange={(event) => setDraftSnapshotAt(event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {t(
                    "inventory.counts.snapshotHint",
                    "The expected quantities are frozen as of this moment.",
                  )}
                </p>
              </div>
              {canReadCategories ? (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="count-category">
                    {t("inventory.common.category", "Category")}
                  </Label>
                  <CategorySelect
                    id="count-category"
                    value={draftCategoryId}
                    canRead={canReadCategories}
                    onChange={setDraftCategoryId}
                  />
                </div>
              ) : null}
            </div>

            {draftWarehouseId !== null && draftLocations.length > 0 ? (
              <fieldset className="flex flex-col gap-2">
                <legend className="text-sm font-medium">
                  {t("inventory.counts.scopeBins", "Bins in scope")}
                </legend>
                <p className="text-xs text-muted-foreground">
                  {t("inventory.counts.scopeBinsHint", "Leave all unchecked to count every bin.")}
                </p>
                <div className="flex flex-wrap gap-3">
                  {draftLocations.map((location) => (
                    <div key={location.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`count-bin-${location.id}`}
                        checked={draftLocationIds.includes(location.id)}
                        onCheckedChange={(checked) =>
                          setDraftLocationIds((current) =>
                            checked === true
                              ? [...current, location.id]
                              : current.filter((id) => id !== location.id),
                          )
                        }
                      />
                      <Label htmlFor={`count-bin-${location.id}`} className="font-normal">
                        {location.code}
                      </Label>
                    </div>
                  ))}
                </div>
              </fieldset>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                loading={createMutation.isPending}
                disabled={createMutation.isPending}
                onClick={submitCreate}
              >
                {t("inventory.counts.generate", "Generate")}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
                {t("actions.cancel")}
              </Button>
            </div>
            {formError !== null ? (
              <p role="alert" className="text-sm text-destructive">
                {formError}
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="count-status-filter">{t("inventory.common.status", "Status")}</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id="count-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("inventory.common.all", "All")}</SelectItem>
              {COUNT_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`inventory.countStatus.${value}`, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="count-warehouse-filter">
            {t("inventory.common.warehouse", "Warehouse")}
          </Label>
          <Select value={warehouseFilter} onValueChange={setWarehouseFilter}>
            <SelectTrigger id="count-warehouse-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("inventory.common.all", "All")}</SelectItem>
              {warehouses.map((warehouse) => (
                <SelectItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.code} — {warehouse.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canReadCategories ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="count-category-filter">
              {t("inventory.common.category", "Category")}
            </Label>
            <CategorySelect
              id="count-category-filter"
              value={categoryFilter}
              canRead={canReadCategories}
              onChange={setCategoryFilter}
            />
          </div>
        ) : null}
      </div>

      <DataTable
        columns={columns}
        query={query}
        getRowId={(row) => row.id}
        onRowClick={(row) => onOpenWorksheet?.(row.id, row.status)}
        storageKey="inventory.counts.list.columns"
        emptyMessage={t("inventory.counts.empty", "No count worksheets yet.")}
        errorMessage={t("inventory.counts.loadError", "Worksheets could not be loaded.")}
      />
    </div>
  );
}
